import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Plus, MapPin, CalendarDays, X, Loader2, CloudLightning, Globe2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import MultilingualEditor from "@/components/ui/MultilingualEditor";
import { packMl, hasMlContent } from "@/lib/ml";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

export const DISASTER_TYPES = [
  ["earthquake", "Earthquake"], ["flood", "Flood"], ["cyclone", "Cyclone"], ["hurricane", "Hurricane"],
  ["tornado", "Tornado"], ["tsunami", "Tsunami"], ["wildfire", "Wildfire"], ["volcano", "Volcano"],
  ["landslide", "Landslide"], ["avalanche", "Avalanche"], ["drought", "Drought"],
  ["extreme_heat", "Extreme heat"], ["extreme_cold", "Extreme cold"],
  ["aircraft", "Aircraft accident"], ["train", "Train accident"], ["bus", "Bus accident"],
  ["ship", "Ship accident"], ["building_collapse", "Building collapse"], ["industrial", "Industrial accident"],
  ["mine", "Mine disaster"], ["infrastructure", "Infrastructure failure"],
  ["conflict", "Conflict-related"], ["mass_casualty", "Mass casualty event"], ["other", "Other"],
];
export const TYPE_LABELS = Object.fromEntries(DISASTER_TYPES);

const STATUS_STYLES = {
  active: "bg-[color:var(--rust)] text-[color:var(--bone)]",
  recovery: "bg-[color:var(--sepia)] text-[color:var(--parchment)]",
  archived: "bg-[color:var(--sepia)]/60 text-[color:var(--parchment)]",
};

export default function Disasters() {
  const { user } = useAuth();
  const [disasters, setDisasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: "", type: "flood", start_date: "", country: "", region: "", city: "" });
  const [overview, setOverview] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/v1/disasters`);
      setDisasters(data.disasters);
    } catch {
      setDisasters([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function create(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/v1/disasters", {
        name: f.name.trim(),
        type: f.type,
        start_date: f.start_date.trim(),
        country: f.country.trim(),
        region: f.region.trim() || undefined,
        city: f.city.trim() || undefined,
        overview: hasMlContent(overview) ? packMl(overview) : undefined,
      });
      toast.success("Disaster page created.");
      setCreating(false);
      setF({ name: "", type: "flood", start_date: "", country: "", region: "", city: "" });
      setOverview("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  const inputCls = "w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-2 focus:outline-none focus:border-[color:var(--rust)]";

  return (
    <main data-testid="disasters-page" className="pt-32 md:pt-40">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-14">
        <div className="overline text-[color:var(--ink-soft)] mb-6">§ Disasters</div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease }}
          className="font-serif-display leading-[0.95] tracking-[-0.02em]"
          style={{ fontSize: "clamp(44px, 7vw, 110px)" }}
        >
          When the world <span className="font-italic-serif italic text-[color:var(--ink-soft)]">breaks open.</span>
        </motion.h1>
        <p className="mt-6 max-w-2xl text-[17px] leading-[1.6] text-[color:var(--ink-soft)]">
          Structured pages for earthquakes, floods, storms and tragedies — connected to the families
          searching for someone, the news as it unfolded, and the memorials that remain.
        </p>
        {user && (
          <button onClick={() => setCreating(true)} className="btn-wood mt-8" data-testid="disaster-create-open">
            <Plus size={15} /> Document a disaster
          </button>
        )}
        <Link to="/map" className={`btn-ghost ${user ? "ml-3" : "mt-8"}`} data-testid="disasters-map-link">
          <Globe2 size={15} /> World map
        </Link>
      </section>

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 bg-[color:var(--ink)]/60 backdrop-blur-sm flex items-center justify-center p-6" style={{ zIndex: 60 }} onClick={() => setCreating(false)}>
          <form
            onSubmit={create}
            onClick={(e) => e.stopPropagation()}
            className="border border-[color:var(--sepia)]/40 max-w-xl w-full p-7 max-h-[85vh] overflow-y-auto shadow-[0_32px_80px_-20px_rgba(43,28,15,0.6)]"
            style={{ background: "var(--parchment)" }}
            data-testid="disaster-form"
          >
            <div className="flex items-start justify-between mb-5">
              <h2 className="heritage-title text-2xl">Document a disaster</h2>
              <button type="button" onClick={() => setCreating(false)} className="p-1 hover:text-[color:var(--rust)]"><X size={16} /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block md:col-span-2">
                <span className="overline block mb-1">Name *</span>
                <input data-testid="disaster-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="2026 Alpine Flash Floods" className={inputCls} required />
              </label>
              <label className="block">
                <span className="overline block mb-1">Type</span>
                <select data-testid="disaster-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inputCls}>
                  {DISASTER_TYPES.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="overline block mb-1">Began *</span>
                <input data-testid="disaster-date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} placeholder="2026-08-10" className={inputCls} required />
              </label>
              <label className="block">
                <span className="overline block mb-1">Country *</span>
                <input data-testid="disaster-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className={inputCls} required />
              </label>
              <label className="block">
                <span className="overline block mb-1">State / Region</span>
                <input data-testid="disaster-region" value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} className={inputCls} />
              </label>
            </div>
            <div className="mt-5">
              <MultilingualEditor
                as="textarea"
                rows={3}
                testid="disaster-overview"
                label="What happened"
                value={overview}
                onChange={setOverview}
                maxLength={5000}
                placeholder="A clear, careful account — facts only, sources to follow…"
              />
            </div>
            <button type="submit" data-testid="disaster-submit" disabled={saving} className="btn-wood mt-6 disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create the page
            </button>
          </form>
        </div>
      )}

      {/* Grid */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24">
        {loading ? (
          <div className="py-16 text-center text-[color:var(--ink-soft)]">Opening the record…</div>
        ) : disasters.length === 0 ? (
          <div className="border border-dashed border-[color:var(--ink-line-strong)] p-14 text-center" data-testid="disasters-empty">
            <CloudLightning size={26} className="mx-auto text-[color:var(--sepia)]" />
            <p className="mt-3 font-italic-serif italic text-xl text-[color:var(--ink-soft)]">
              No disasters documented — may the record stay empty.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="disasters-grid">
            {disasters.map((d, i) => (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i, 6) * 0.05, ease }}
              >
                <Link
                  to={`/disasters/${d.id}`}
                  data-testid={`disaster-card-${d.id}`}
                  className="group block border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)] p-6 h-full hover:shadow-[0_16px_44px_-24px_rgba(43,28,15,0.5)] transition-shadow"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`px-2 py-0.5 font-heritage-caps text-[10px] tracking-[0.14em] ${STATUS_STYLES[d.status]}`}>
                      {d.status}
                    </span>
                    <span className="numeric-index text-[color:var(--ink-soft)]">{TYPE_LABELS[d.type] || d.type}</span>
                  </div>
                  <h3 className="mt-3 font-serif-display text-2xl leading-snug group-hover:text-[color:var(--rust)] transition-colors">{d.name}</h3>
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-[color:var(--ink-soft)]">
                    <MapPin size={11} /> {[d.city, d.region, d.country].filter(Boolean).join(", ")}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-[color:var(--ink-soft)]">
                    <CalendarDays size={11} /> {d.start_date}
                  </div>
                  {d.case_stats?.total > 0 && (
                    <div className="mt-4 pt-3 border-t border-[color:var(--sepia)]/20 flex gap-4 numeric-index text-[color:var(--ink-soft)]">
                      <span>{d.case_stats.missing} missing</span>
                      <span>{d.case_stats.found} found</span>
                    </div>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
